/**
 * Twilio Voice SDK wrapper for the agent softphone.
 *
 * Encapsulates the lifecycle of a Twilio.Device + an active Call:
 *   - Fetch a short-lived access token from our backend (/api/voice/token)
 *   - Initialize Device with that token, wire up Device events
 *   - Refresh the token before expiry (and on `tokenWillExpire`)
 *   - Provide a small surface: call(), hangup(), mute(), state observable
 *
 * The actual TaskRouter Worker SDK (reservations + activity) lives in a
 * separate module; this one is voice-only. Both share the same access token
 * from the backend.
 *
 * Why a class: the Voice SDK is stateful (single Device per page session).
 * React-friendly subscribe pattern so SoftphoneView can re-render on changes.
 */

import { Device, Call } from "@twilio/voice-sdk";
import { api } from "./api";
import { parseUSPhone } from "./phone";

export type SoftphoneState =
  | { kind: "idle" }
  | { kind: "initializing" }
  | { kind: "ready"; identity: string }
  | { kind: "ringing-out"; to: string; call: Call }
  | { kind: "ringing-in"; from: string; call: Call }
  | { kind: "in-call"; remote: string; call: Call; muted: boolean }
  | { kind: "wrapping" }
  | { kind: "error"; message: string };

type Listener = (s: SoftphoneState) => void;

type TokenResponse = {
  token: string;
  identity: string;
  expiresAt: string;
  workspaceSid: string;
  workerSid: string;
};

export class Softphone {
  private device: Device | null = null;
  private state: SoftphoneState = { kind: "idle" };
  private listeners = new Set<Listener>();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private wrapTimer: ReturnType<typeof setTimeout> | null = null;
  /** Sticky identity once token is minted — survives call-lifecycle transitions back to `ready`. */
  private identity: string = "agent";
  /** In-flight start() promise so concurrent calls return the same work (React StrictMode double-mount). */
  private starting: Promise<void> | null = null;

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  private set(next: SoftphoneState): void {
    this.state = next;
    for (const fn of this.listeners) fn(next);
  }

  /**
   * Start the Device. Fetches a token, registers, sets state=ready.
   * Idempotent: concurrent calls share the same in-flight Promise so React
   * StrictMode double-mount doesn't create two Devices.
   */
  async start(): Promise<void> {
    if (this.device) return;
    if (this.starting) return this.starting;
    this.starting = this.doStart();
    try {
      await this.starting;
    } finally {
      this.starting = null;
    }
  }

  private async doStart(): Promise<void> {
    this.set({ kind: "initializing" });
    try {
      const t = await api<TokenResponse>("/api/voice/token", { method: "POST" });
      this.identity = t.identity;
      this.device = new Device(t.token, {
        // Use Opus + PCMU; default codecs are fine but explicit = predictable
        codecPreferences: ["opus" as Call.Codec, "pcmu" as Call.Codec],
        // Allow incoming via the SDK's TwiML <Dial><Client> path
        allowIncomingWhileBusy: false,
        logLevel: 1,
      });

      this.device.on("registered", () => this.set({ kind: "ready", identity: this.identity }));
      this.device.on("error", (err: { message: string }) => {
        this.set({ kind: "error", message: err.message });
      });
      this.device.on("incoming", (call: Call) => {
        const from = (call.parameters as Record<string, string> | undefined)?.["From"] ?? "unknown";
        this.set({ kind: "ringing-in", from, call });
        this.wireCallEvents(call, from);
      });
      this.device.on("tokenWillExpire", () => void this.refreshToken());

      await this.device.register();
      this.scheduleRefresh(t.expiresAt);
    } catch (e) {
      this.set({ kind: "error", message: (e as Error).message });
    }
  }

  /**
   * Place an outbound call. Input may be any human format; `fromOverride`
   * (when present) is the E.164 number the user picked as caller-id, which
   * the backend will validate against their authorized numbers before
   * actually dialing.
   */
  async call(rawInput: string, fromOverride?: string | null): Promise<void> {
    if (!this.device) throw new Error("softphone not started");
    const parsed = parseUSPhone(rawInput);
    if (!parsed.ok) {
      this.set({ kind: "error", message: `invalid number: ${parsed.reason}` });
      return;
    }
    const params: Record<string, string> = { To: parsed.e164 };
    if (fromOverride) params.From = fromOverride;
    const call = await this.device.connect({ params });
    this.set({ kind: "ringing-out", to: parsed.e164, call });
    this.wireCallEvents(call, parsed.e164);
  }

  hangup(): void {
    if (this.state.kind === "in-call" || this.state.kind === "ringing-out" || this.state.kind === "ringing-in") {
      this.state.call.disconnect();
    }
  }

  accept(): void {
    if (this.state.kind === "ringing-in") {
      this.state.call.accept();
    }
  }

  mute(muted: boolean): void {
    if (this.state.kind === "in-call") {
      this.state.call.mute(muted);
      this.set({ ...this.state, muted });
    }
  }

  shutdown(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    if (this.wrapTimer) clearTimeout(this.wrapTimer);
    this.refreshTimer = null;
    this.wrapTimer = null;
    // Order matters: notify listeners idle BEFORE clearing them so React can
    // observe the transition, then drop the device + listeners. Drop the
    // device reference BEFORE clearing listeners so any post-destroy event
    // callbacks (Twilio SDK fires "unregistered" etc on destroy) that hit
    // this.set() find an empty listeners Set and become no-ops.
    this.set({ kind: "idle" });
    const dev = this.device;
    this.device = null;
    dev?.removeAllListeners();
    dev?.destroy();
    this.listeners.clear();
  }

  // ---- private helpers ----

  private wireCallEvents(call: Call, remote: string): void {
    call.on("accept", () => this.set({ kind: "in-call", remote, call, muted: false }));
    call.on("disconnect", () => {
      this.set({ kind: "wrapping" });
      // Auto-return to ready after a brief wrap-up window so the UI doesn't
      // leave the operator stuck. PBX users have no manual wrap-up; ACD will
      // expose an explicit "I'm done" button.
      if (this.wrapTimer) clearTimeout(this.wrapTimer);
      this.wrapTimer = setTimeout(() => {
        if (this.state.kind === "wrapping") {
          this.set({ kind: "ready", identity: this.identity });
        }
      }, 1500);
    });
    call.on("cancel", () => this.set({ kind: "ready", identity: this.identity }));
    call.on("reject", () => this.set({ kind: "ready", identity: this.identity }));
    call.on("error", (err: { message: string }) => this.set({ kind: "error", message: err.message }));
  }

  private scheduleRefresh(expiresAtIso: string): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    const expiresAt = Date.parse(expiresAtIso);
    const refreshAt = expiresAt - 60_000; // refresh 1 min before expiry
    const wait = Math.max(refreshAt - Date.now(), 30_000); // at minimum 30s out
    this.refreshTimer = setTimeout(() => void this.refreshToken(), wait);
  }

  private async refreshToken(): Promise<void> {
    if (!this.device) return;
    try {
      const t = await api<TokenResponse>("/api/voice/token", { method: "POST" });
      this.device.updateToken(t.token);
      this.scheduleRefresh(t.expiresAt);
    } catch (e) {
      this.set({ kind: "error", message: `token refresh failed: ${(e as Error).message}` });
    }
  }
}
